// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from '@insite/client-framework/Store/ApplicationState';
import translate from "@insite/client-framework/Translate";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { getScratchAndDentParent } from "@plumberssupply/App/Store/Selectors/ScratchAndDentSelectors";
import ScratchAndDent from "@plumberssupply/XDL/Icons/S&D";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    inPDP?: boolean;
    extendedStyles?: ScratchAndDentStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    isTabletScreen: getXngCommon(state).isTabletScreen,
    isMobileScreen: getXngCommon(state).isMobileScreen,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ScratchAndDentStyles {
    wrapper?: InjectableCss;
    badge?: IconPresentationProps;
    link?: LinkPresentationProps;
}

export const scratchAndDentStyles: ScratchAndDentStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-top: 8px;
        `,
    },
    badge: {
        src: ScratchAndDent,
        size: 31,
        css: css`
            height: 18px;
            margin-right: 4px;
        `,
    },
    link: {
        typographyProps: {
            size: 12,
            lineHeight: "17px",
        },
        css: css`
            @media print {
                display: initial !important;
            }
        `,
    },
};

const ProductScratchAndDentBadge: FC<Props> = ({ productContext, inPDP, isTabletScreen, isMobileScreen, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(scratchAndDentStyles, extendedStyles));

    if (!getScratchAndDentParent(productContext.product.properties)) {
        return null;
    }

    const productDetailPath = productContext.productInfo.productDetailPath;

    const navigateToScratchAndDentCarousel = () => {
        const scratchAndDentProductsCarousel = document.querySelector("#productCarousel_scratchAndDent") as HTMLElement | null;
        if (scratchAndDentProductsCarousel) {
            window.scrollTo({
                top: scratchAndDentProductsCarousel?.offsetTop + (isMobileScreen ? 30 : isTabletScreen ? -82 : -112),
                left: 0,
                behavior: "smooth",
            });
        }
    }
    const linkProps = inPDP ? { onClick: navigateToScratchAndDentCarousel } : { href: `${productDetailPath}?ScratchAndDent` };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Icon {...styles.badge} />
            <Link
                {...linkProps}
                aria-label={translate("Discount Units Available")}
                {...styles.link}
            >
                {translate("Discount Units Available")}
            </Link>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductScratchAndDentBadge));
